import { StrictMode } from 'react'
import './i18n'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'

// When we redeploy, the old index.html (that a user has cached in-memory)
// references JS chunks with hashes that no longer exist on the server. The
// lazy import then fails with "Failed to fetch dynamically imported module" /
// "Expected a JavaScript-or-Wasm module script but the server responded with
// a MIME type of 'text/html'". Auto-reload once to pull the fresh index.html
// (which references the new chunks). We only retry once per session to avoid
// infinite loops if the real problem is something else.
const RELOAD_FLAG = 'io.chunkReloadedAt';
function handleChunkLoadError(err: unknown): void {
  const msg = err instanceof Error ? err.message || '' : String(err ?? '');
  const isChunkError =
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Expected a JavaScript-or-Wasm module script/i.test(msg) ||
    /Importing a module script failed/i.test(msg);
  if (!isChunkError) return;
  const lastReload = Number(sessionStorage.getItem(RELOAD_FLAG) ?? '0');
  if (Date.now() - lastReload < 30_000) return; // guard: already reloaded recently
  sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  console.warn('[io] Stale build detected — reloading to fetch new assets.');
  window.location.reload();
}

window.addEventListener('error', (event) => handleChunkLoadError(event.error ?? event.message));
window.addEventListener('unhandledrejection', (event) => handleChunkLoadError(event.reason));

// Auto-login from URL fragment.
// When the iOS app opens a web URL via Capacitor's in-app Safari View
// Controller (e.g. for Stripe checkout, terms, plan upgrade), it
// appends the user's saved access + refresh tokens as fragment
// params: `#access_token=X&refresh_token=Y`. We pick those up here,
// store them in localStorage so the AuthContext sees an authenticated
// session on its first read, then strip them from the URL so they
// don't leak through history/copy-paste/bookmarks.
//
// Why fragment (not query): fragments are NEVER sent to the server,
// so the tokens stay client-side only. Server logs / Vercel access
// logs / referer headers won't contain them.
(function autoLoginFromUrlFragment() {
  try {
    if (typeof window === 'undefined' || !window.location.hash) return;
    const fragment = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(fragment);
    const access  = params.get('access_token');
    const refresh = params.get('refresh_token');
    if (!access && !refresh) return;
    // Determine scope from the path (admin or user) — same logic
    // the existing tokens module uses.
    const scope: 'admin' | 'user' =
      window.location.pathname.startsWith('/admin') ? 'admin' : 'user';
    // Storage key format must match `keyFor()` in src/api/http.ts:
    // `io.${scope}.${kind}Token` — e.g. `io.user.accessToken`.
    if (access)  localStorage.setItem(`io.${scope}.accessToken`,  access);
    if (refresh) localStorage.setItem(`io.${scope}.refreshToken`, refresh);
    // Strip the fragment from the URL so the tokens don't sit there
    // visible. Keep any non-auth fragment params if present.
    params.delete('access_token');
    params.delete('refresh_token');
    const remaining = params.toString();
    const newHash = remaining ? `#${remaining}` : '';
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + window.location.search + newHash,
    );
    // eslint-disable-next-line no-console
    console.info('[io] auto-logged in from URL fragment');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[io] auto-login from fragment failed:', e);
  }
})();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
